import { useId } from "react";
import { LabelContext, TextContext } from "react-aria-components";

import { type InputLabelTopV2Props } from "../InputLabelTopV2";
import { type InputAlertsProps } from "../InputAlerts";

type RenderProps = {
  labelledBy: string | undefined;
  describedBy: string | undefined;
};

export type ReactAriaProvidersProps = {
  fieldID?: string;
  externallyLabelledBy?: string;
  externallyDescribedBy?: string;
  labelProps?: Omit<InputLabelTopV2Props, "children">;
  alerts?: InputAlertsProps["alerts"];
  children: (props: RenderProps) => JSX.Element;
};

function ReactAriaProviders(props: ReactAriaProvidersProps) {
  const {
    fieldID,
    externallyLabelledBy,
    externallyDescribedBy,
    labelProps,
    alerts,
    children,
  } = props;

  const hasLabel = Boolean(labelProps?.label);
  const hasLabelHint = Boolean(labelProps?.labelHint);
  const hasErrorMessage = Boolean(alerts);

  const labelID = useId();
  const descriptionID = useId();
  const errorMessageID = useId();

  const labelledBy =
    [hasLabel ? labelID : undefined, externallyLabelledBy]
      .filter(Boolean)
      .join(" ") || undefined;

  const describedBy =
    [
      hasLabelHint ? descriptionID : undefined,
      hasErrorMessage ? errorMessageID : undefined,
      externallyDescribedBy,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const labelContextValue = {
    id: labelID,
    htmlFor: fieldID,
  };

  const textContextValue = {
    slots: {
      description: { id: descriptionID },
      errorMessage: { id: errorMessageID },
    },
  };

  return (
    <LabelContext.Provider value={labelContextValue}>
      <TextContext.Provider value={textContextValue}>
        {children({ labelledBy, describedBy })}
      </TextContext.Provider>
    </LabelContext.Provider>
  );
}

export default ReactAriaProviders;
